'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface RoutePrefetcherProps {
  routes: string[];
}

/**
 * Precarga las rutas del menú para que al tocarlas no haya que ir a buscar el
 * bundle desde cero.
 *
 * Dos cosas que antes lo dejaban sin efecto y ahora sí importan:
 *
 * 1. `router.prefetch` de una ruta dinámica trae el árbol sólo hasta la primera
 *    frontera de carga. Todas las rutas de este sitio son dinámicas (el layout
 *    es `force-dynamic` porque el tenant sale de la cabecera `Host`) y hasta
 *    ahora ninguna tenía `loading.tsx`: sin frontera no había nada que
 *    precargar, así que estas llamadas no guardaban lo que hacía falta. Con el
 *    `loading.tsx` de cada ruta sí lo hay.
 *
 * 2. Se disparaban todas juntas al hidratar, que es justo cuando la home está
 *    peleando por el ancho de banda que le falta. Ahora esperan a que el
 *    navegador esté ocioso y salen de a una.
 *
 * Y no se precarga nada si el usuario pidió ahorrar datos o está en una red
 * lenta: ahí adelantar nueve rutas cuesta más de lo que ahorra.
 * `navigator.connection` no existe en Safari, así que la ausencia del dato se
 * trata como "seguí", no como "abortá".
 */
export default function RoutePrefetcher({ routes }: RoutePrefetcherProps) {
  const router = useRouter();

  useEffect(() => {
    const conexion = (navigator as any)?.connection;
    if (conexion?.saveData) return;
    if (typeof conexion?.effectiveType === 'string' && /2g$/.test(conexion.effectiveType)) return;

    let vivo = true;
    const pendientes = [...routes];

    // `requestIdleCallback` no existe en Safari/iOS; ahí se usa un respiro fijo.
    const ocioso = (cb: () => void) =>
      typeof (window as any).requestIdleCallback === 'function'
        ? (window as any).requestIdleCallback(cb, { timeout: 2000 })
        : window.setTimeout(cb, 300);

    const siguiente = () => {
      if (!vivo) return;
      const ruta = pendientes.shift();
      if (!ruta) return;
      router.prefetch(ruta);
      ocioso(siguiente);
    };

    ocioso(siguiente);
    return () => { vivo = false; };
    // Corre una sola vez: la lista es literal y no cambia entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
